<script setup>
import { ref, computed, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRouter, useRoute } from 'vue-router';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

// 删除弹窗
const deleteDialog = ref(null);

// 助手列表（删除后跳转用）
const assistants = useMapGetter('pilotAssistants/getRecords');
const assistantId = computed(() => props.assistant?.id);

// ========== 基本设置 ==========
const basicState = ref({
  name: '',
  description: '',
  // TODO: 功能暂时禁用，后续开放
  // features: {
  //   memories: false,
  //   citations: false,
  // },
});

const isBasicSaving = ref(false);

// ========== 思考过程显示 ==========
const thinkingVisibilityState = ref({
  enabled: false,
});

const isThinkingVisibilitySaving = ref(false);

// 初始化基本设置
watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) {
      const { config = {} } = newAssistant;
      basicState.value = {
        name: newAssistant.name || '',
        description: newAssistant.description || '',
        // TODO: 功能暂时禁用，后续开放
        // features: {
        //   memories: config.feature_memory || false,
        //   citations: config.feature_citation || false,
        // },
      };
      thinkingVisibilityState.value = {
        enabled: newAssistant.config?.show_thinking_process === true,
      };
    }
  },
  { immediate: true }
);

// 保存基本设置
const handleSaveBasic = async () => {
  if (!basicState.value.name.trim()) {
    useAlert('名称不能为空');
    return;
  }

  isBasicSaving.value = true;
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: {
        name: basicState.value.name,
        description: basicState.value.description,
        // TODO: 功能暂时禁用，后续开放
        // config: {
        //   ...props.assistant.config,
        //   feature_memory: basicState.value.features.memories,
        //   feature_citation: basicState.value.features.citations,
        // },
      },
    });
    useAlert(t('PILOT.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('PILOT.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  } finally {
    isBasicSaving.value = false;
  }
};

const handleToggleThinkingVisibility = async value => {
  if (!props.assistant?.id || isThinkingVisibilitySaving.value) return;

  const previousValue = thinkingVisibilityState.value.enabled;
  thinkingVisibilityState.value.enabled = value;
  isThinkingVisibilitySaving.value = true;

  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: {
        config: {
          ...props.assistant.config,
          show_thinking_process: value,
        },
      },
    });
    useAlert('AI 思考过程显示设置已保存');
  } catch (error) {
    thinkingVisibilityState.value.enabled = previousValue;
    useAlert(error?.message || 'AI 思考过程显示设置保存失败');
  } finally {
    isThinkingVisibilitySaving.value = false;
  }
};

// ========== 系统设置 ==========
const systemState = ref({
  temperature: 0.7,
});

const isSystemSaving = ref(false);

// 初始化系统设置
watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) {
      const { config = {} } = newAssistant;
      systemState.value = {
        temperature: config.temperature ?? 0.7,
      };
    }
  },
  { immediate: true }
);

// 保存系统设置
const handleSaveSystem = async () => {
  isSystemSaving.value = true;
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: {
        config: {
          ...props.assistant.config,
          temperature: systemState.value.temperature,
        },
      },
    });
    useAlert(t('PILOT.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('PILOT.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  } finally {
    isSystemSaving.value = false;
  }
};

// ========== 删除助手 ==========
const handleDelete = () => {
  deleteDialog.value?.open();
};

const handleDeleteSuccess = () => {
  const remainingAssistants = assistants.value.filter(
    a => a.id !== assistantId.value
  );

  if (remainingAssistants.length > 0) {
    const nextAssistant = remainingAssistants[0];
    router.push({
      name: 'pilot_assistant_config_section',
      params: {
        accountId: route.params.accountId,
        assistantId: nextAssistant.id,
        section: 'role',
      },
    });
  } else {
    router.push({
      name: 'pilot_assistants_create_index',
      params: { accountId: route.params.accountId },
    });
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- 一级标题 -->
    <h2 class="text-base font-semibold text-n-slate-12 pt-4">
      {{ t('PILOT.ASSISTANTS.CONFIG.NAV.SETTINGS') }}
    </h2>

    <!-- 基本设置 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center gap-2 mb-3">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('PILOT.ASSISTANTS.SETTINGS.BASIC_SETTINGS.TITLE') }}
        </h3>
        <span
          v-tooltip="'助手的基本信息和功能开关'"
          class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
        />
      </div>

      <div class="flex flex-col gap-4">
        <!-- 名称 -->
        <Input
          v-model="basicState.name"
          :label="t('PILOT.ASSISTANTS.FORM.NAME.LABEL')"
          :placeholder="t('PILOT.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
        />

        <!-- 描述 -->
        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-1 block">
            {{ t('PILOT.ASSISTANTS.FORM.DESCRIPTION.LABEL') }}
          </label>
          <TextArea
            v-model="basicState.description"
            :placeholder="t('PILOT.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
            :auto-height="true"
            min-height="80px"
            custom-text-area-wrapper-class="!bg-white dark:!bg-n-slate-800"
          />
        </div>

        <!-- TODO: 功能开关暂时禁用，后续开放 -->
        <!-- <div>
          <label class="text-sm font-medium text-n-slate-12 mb-2 block">
            {{ t('PILOT.ASSISTANTS.FORM.FEATURES.TITLE') }}
          </label>
          <div class="flex flex-col gap-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="basicState.features.memories"
                type="checkbox"
                class="rounded border-n-slate-6"
              />
              <span class="text-sm text-n-slate-11">
                {{ t('PILOT.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
              </span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="basicState.features.citations"
                type="checkbox"
                class="rounded border-n-slate-6"
              />
              <span class="text-sm text-n-slate-11">
                {{ t('PILOT.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
              </span>
            </label>
          </div>
        </div> -->

        <!-- 保存按钮 -->
        <div class="flex justify-end">
          <Button
            :label="t('PILOT.ASSISTANTS.FORM.UPDATE')"
            :is-loading="isBasicSaving"
            @click="handleSaveBasic"
          />
        </div>
      </div>
    </section>

    <!-- 思考过程显示 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center gap-2 mb-3">
        <h3 class="text-sm font-medium text-n-slate-12">
          AI 思考过程
        </h3>
        <span
          v-tooltip="'控制是否在用户端和后台会话中展示 AI 的思考过程。默认关闭。'"
          class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
        />
      </div>

      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <p class="text-sm font-medium text-n-slate-12">
            显示思考过程
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            关闭后，用户端和后台会话都不再展示 AI 思考过程。
          </p>
        </div>

        <Switch
          :model-value="thinkingVisibilityState.enabled"
          :disabled="isThinkingVisibilitySaving"
          @update:model-value="handleToggleThinkingVisibility"
        />
      </div>
    </section>

    <!-- 系统设置 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center gap-2 mb-3">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('PILOT.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.TITLE') }}
        </h3>
        <span
          v-tooltip="'助手的系统消息和响应参数'"
          class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
        />
      </div>

      <div class="flex flex-col gap-4">
        <!-- 响应温度 -->
        <div>
          <label class="text-sm font-medium text-n-slate-12 mb-2 block">
            {{ t('PILOT.ASSISTANTS.FORM.TEMPERATURE.LABEL') }}
          </label>
          <div class="flex items-center gap-4">
            <input
              v-model="systemState.temperature"
              type="range"
              min="0"
              max="1"
              step="0.1"
              class="w-full"
            />
            <span class="text-sm text-n-slate-12 w-8">{{
              systemState.temperature
            }}</span>
          </div>
          <p class="text-sm text-n-slate-11 italic mt-1">
            {{ t('PILOT.ASSISTANTS.FORM.TEMPERATURE.DESCRIPTION') }}
          </p>
        </div>

        <!-- 保存按钮 -->
        <div class="flex justify-end">
          <Button
            :label="t('PILOT.ASSISTANTS.FORM.UPDATE')"
            :is-loading="isSystemSaving"
            @click="handleSaveSystem"
          />
        </div>
      </div>
    </section>

    <!-- 删除助手 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center justify-between w-full gap-4">
        <div class="flex flex-col gap-1">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('PILOT.ASSISTANTS.SETTINGS.DELETE.TITLE') }}
          </h3>
          <span class="text-sm text-n-slate-11">
            {{ t('PILOT.ASSISTANTS.SETTINGS.DELETE.DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="
            t('PILOT.ASSISTANTS.SETTINGS.DELETE.BUTTON_TEXT', {
              assistantName: assistant.name,
            })
          "
          color="ruby"
          size="sm"
          @click="handleDelete"
        />
      </div>
    </section>

    <!-- 删除确认弹窗 -->
    <DeleteDialog
      v-if="assistant"
      ref="deleteDialog"
      :entity="assistant"
      type="Assistants"
      translation-key="ASSISTANTS"
      @delete-success="handleDeleteSuccess"
    />
  </div>
</template>
